(() => {
  const SELECTOR = 'variant-picker';
  const CONFIG_SELECTOR = '.jd1-mix-config';
  const initialized = new WeakSet();

  function parseConfig(picker) {
    const node = picker.querySelector(CONFIG_SELECTOR);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('[JD1 Mix Match] Invalid config', error);
      return null;
    }
  }

  function findProductRoot(picker) {
    return picker.closest('.product-info__inner') || picker.closest('.product-info') || picker.parentElement;
  }

  function findProductForm(picker) {
    const root = findProductRoot(picker);
    return root?.querySelector('form.js-product-form-main') || document.querySelector('form.js-product-form-main');
  }

  function getAddButton(form) {
    return form?.querySelector('.js-add-to-cart');
  }

  function ensureHiddenProperty(form) {
    if (!form) return null;
    let input = form.querySelector('input[name="properties[_jd1_mix_components]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_jd1_mix_components]';
      form.appendChild(input);
    }
    return input;
  }

  function totalFor(state) {
    let total = 0;
    state.forEach((qty) => { total += qty; });
    return total;
  }

  function aggregatePayload(config, states) {
    const grouped = new Map();

    const add = (id, quantity) => {
      if (!id || !quantity) return;
      grouped.set(id, (grouped.get(id) || 0) + quantity);
    };

    (config.fixedComponents || []).forEach((item) => add(item.id, Number(item.quantity) || 0));

    (config.groups || []).forEach((group, index) => {
      const state = states[index];
      if (!state) return;
      state.forEach((quantity, value) => {
        add(group.values?.[value], quantity);
      });
    });

    return [...grouped.entries()].map(([id, quantity]) => ({ id, quantity }));
  }

  function variantNumericId(gid) {
    const match = String(gid || '').match(/ProductVariant\/(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  function createKitId(config) {
    const base = config.kitHandle || 'kit';
    const random = Math.random().toString(36).slice(2, 8);
    return base + '-' + Date.now() + '-' + random;
  }

  async function addMixItems({ config, states, addButton }) {
    const payload = aggregatePayload(config, states);
    const kitId = createKitId(config);
    const kitName = config.kitName || 'Kit';

    const items = payload.map((item) => ({
      id: variantNumericId(item.id),
      quantity: item.quantity,
      properties: {
        Kit: kitName,
        _jd1_kit_id: kitId,
        _jd1_kit_name: kitName,
        _jd1_kit_handle: config.kitHandle || ''
      }
    })).filter((item) => item.id && item.quantity > 0);

    if (!items.length) throw new Error('No hay productos seleccionados para agregar.');

    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer
      ? cartDrawer.dataset.section + ',cart-icon-bubble'
      : 'cart-icon-bubble';

    const response = await fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname
      })
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'No se pudo agregar el kit al carrito.');
    }

    if (cartDrawer && data.sections) {
      await cartDrawer.renderContents(data, true, addButton);
    } else {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
        detail: { opener: addButton }
      }));
    }
  }

  function mediaNumericId(gid) {
    const match = String(gid || '').match(/MediaImage\/(\d+)$/);
    return match ? match[1] : null;
  }

  function showMediaForValue(config, value) {
    const mediaId = mediaNumericId(config.mediaByValue?.[value]);
    if (!mediaId) return;

    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    const mediaItem = gallery.querySelector('[data-media-id="' + mediaId + '"]');
    if (!mediaItem) return;

    if (typeof gallery.customSetActiveMedia === 'function') {
      gallery.customSetActiveMedia(mediaItem, true);
      return;
    }

    mediaItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  function cleanEditUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('edit_kit');
    url.searchParams.delete('replace_variant');
    window.location.replace(url.toString());
  }

  async function ensureEditKitStillExists(editKitId) {
    if (!editKitId) return true;

    try {
      const response = await fetch(theme.routes.cart + '.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) return true;

      const cart = await response.json();
      const exists = (cart.items || []).some(
        (item) => item.properties?._jd1_kit_id === editKitId
      );

      if (!exists) {
        cleanEditUrl();
        return false;
      }

      return true;
    } catch {
      return true;
    }
  }

  async function loadEditKit(config, groupStates, editKitId, replaceVariantId) {
    const response = await fetch(theme.routes.cart + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('No se pudo cargar el carrito.');

    const cart = await response.json();
    const kitItems = (cart.items || []).filter(
      (item) => item.properties?._jd1_kit_id === editKitId
    );
    const target = kitItems.find(
      (item) => Number(item.variant_id) === Number(replaceVariantId)
    );

    if (!kitItems.length || !target) {
      cleanEditUrl();
      throw new Error('Este kit ya no está en el carrito.');
    }

    const originalLines = kitItems.map((item) => ({
      key: item.key,
      variantId: Number(item.variant_id),
      quantity: Number(item.quantity || 0)
    }));

    kitItems.forEach((item) => {
      groupStates.forEach((groupState) => {
        const match = Object.entries(groupState.group.values || {}).find(
          ([, gid]) => variantNumericId(gid) === Number(item.variant_id)
        );
        if (!match) return;

        const [value] = match;
        groupState.state.set(
          value,
          (groupState.state.get(value) || 0) + Number(item.quantity || 0)
        );
      });
    });

    // "Cambiar este tono" abre el editor dejando libre una unidad
    // del tono desde el que se inició la edición.
    groupStates.forEach((groupState) => {
      const match = Object.entries(groupState.group.values || {}).find(
        ([, gid]) => variantNumericId(gid) === Number(replaceVariantId)
      );
      if (!match) return;

      const [value] = match;
      const current = groupState.state.get(value) || 0;
      if (current <= 1) groupState.state.delete(value);
      else groupState.state.set(value, current - 1);
      groupState.activeValue = value;
    });

    return {
      kitId: editKitId,
      kitName: target.properties?._jd1_kit_name || config.kitName || 'Kit',
      kitHandle: target.properties?._jd1_kit_handle || config.kitHandle || '',
      originalLines
    };
  }

  async function replaceMixItem({ config, states, editContext, addButton }) {
    const payload = aggregatePayload(config, states);
    const desired = new Map();

    payload.forEach((item) => {
      const variantId = variantNumericId(item.id);
      if (!variantId) return;
      desired.set(variantId, Number(item.quantity || 0));
    });

    const originalByVariant = new Map();
    editContext.originalLines.forEach((line) => {
      if (!originalByVariant.has(line.variantId)) {
        originalByVariant.set(line.variantId, []);
      }
      originalByVariant.get(line.variantId).push(line);
    });

    const newItems = [];
    desired.forEach((quantity, variantId) => {
      if (!originalByVariant.has(variantId) && quantity > 0) {
        newItems.push({
          id: variantId,
          quantity,
          properties: {
            Kit: editContext.kitName,
            _jd1_kit_id: editContext.kitId,
            _jd1_kit_name: editContext.kitName,
            _jd1_kit_handle: editContext.kitHandle
          }
        });
      }
    });

    if (newItems.length) {
      const addResponse = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: newItems })
      });

      const addData = await addResponse.json();
      if (!addResponse.ok || addData.status) {
        throw new Error(addData.description || addData.message || 'No se pudieron agregar los nuevos tonos.');
      }
    }

    const updates = {};
    originalByVariant.forEach((lines, variantId) => {
      let remaining = desired.get(variantId) || 0;

      lines.forEach((line, index) => {
        const quantity = index === 0 ? remaining : 0;
        updates[line.key] = quantity;
        remaining = 0;
      });
    });

    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer
      ? cartDrawer.dataset.section + ',cart-icon-bubble'
      : 'cart-icon-bubble';

    const updateResponse = await fetch(theme.routes.cartUpdate || (theme.routes.cart + '/update.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        updates,
        sections,
        sections_url: window.location.pathname
      })
    });

    const updateData = await updateResponse.json();
    if (!updateResponse.ok || updateData.status) {
      throw new Error(updateData.description || updateData.message || 'No se pudieron guardar los cambios del kit.');
    }

    if (cartDrawer && updateData.sections) {
      await cartDrawer.renderContents(updateData, true, addButton);
    } else {
      window.location.href = theme.routes.cart;
    }
  }

  function renderGroup(groupState) {
    const { group, state, fieldset, heading, progress, selected, counter, counterName, counterQty, counterMinus, counterPlus } = groupState;
    const total = totalFor(state);
    const required = Number(group.requiredQuantity) || 1;
    const remaining = Math.max(0, required - total);

    progress.textContent = groupState.editMode
      ? (remaining > 0
          ? 'Selecciona ' + remaining + (remaining === 1 ? ' tono' : ' tonos')
          : required + ' / ' + required + ' seleccionados')
      : total + ' / ' + required + ' seleccionados';

    progress.classList.toggle('is-complete', total === required);

    fieldset.querySelectorAll('label[for]').forEach((label) => {
      const input = document.getElementById(label.htmlFor);
      if (!input) return;

      const qty = state.get(input.value) || 0;
      label.classList.toggle('jd1-mix-swatch-selected', qty > 0);
      label.classList.toggle('jd1-mix-swatch-active', input.value === groupState.activeValue);
      label.setAttribute(
        'aria-label',
        input.value + (qty ? ', ' + qty + ' seleccionados' : '')
      );
    });

    if (counter) {
      const activeValue = groupState.activeValue;
      const activeQty = activeValue ? (state.get(activeValue) || 0) : 0;

      counter.hidden = !activeValue;
      if (activeValue) {
        counterName.textContent = activeValue;
        counterQty.textContent = activeQty;
        counterMinus.disabled = activeQty < 1;
        counterPlus.disabled = total >= required;
      }
    }

    selected.innerHTML = '';
    state.forEach((quantity, value) => {
      if (quantity < 1) return;

      const item = document.createElement('span');
      item.className = 'jd1-mix-chip';

      const text = document.createElement('span');
      text.textContent = value + (quantity > 1 ? ' ×' + quantity : '');
      item.appendChild(text);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = '×';
      remove.setAttribute('aria-label', 'Quitar todas las unidades de ' + value);
      remove.title = 'Quitar todas las unidades de ' + value;

      remove.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        state.delete(value);

        const matchingInput = [...fieldset.querySelectorAll('input[type="radio"]')]
          .find((input) => input.value === value);
        if (matchingInput) matchingInput.checked = false;

        if (groupState.activeValue === value) {
          groupState.activeValue = null;
        }

        groupState.onChange();
      }, true);

      item.appendChild(remove);
      selected.appendChild(item);
    });

    heading.hidden = false;
  }

  function initPicker(picker) {
    if (initialized.has(picker)) return;
    const config = parseConfig(picker);
    if (!config || !Array.isArray(config.groups) || !config.groups.length) return;

    const form = findProductForm(picker);
    const addButton = getAddButton(form);
    const hiddenProperty = ensureHiddenProperty(form);
    const states = {};
    const groupStates = [];
    let hasMulti = false;
    const params = new URLSearchParams(window.location.search);
    const editKitId = params.get('edit_kit');
    const replaceVariantId = params.get('replace_variant');
    const isEditMode = config.mode === 'line_items' && Boolean(editKitId && replaceVariantId);
    let editContext = null;
    let editReady = !isEditMode;

    const updateAll = () => {
      let complete = true;

      groupStates.forEach((groupState) => {
        renderGroup(groupState);
        const total = totalFor(groupState.state);
        if (total !== Number(groupState.group.requiredQuantity)) complete = false;
      });

      const payload = aggregatePayload(config, states);
      if (hiddenProperty) hiddenProperty.value = JSON.stringify(payload);

      if (addButton) {
        if (!addButton.dataset.jd1OriginalText) {
          addButton.dataset.jd1OriginalText = addButton.textContent.trim();
          addButton.dataset.jd1OriginalDisabled = addButton.disabled ? 'true' : 'false';
        }

        if (!complete) {
          const firstIncomplete = groupStates.find((item) => totalFor(item.state) !== Number(item.group.requiredQuantity));
          const done = firstIncomplete ? totalFor(firstIncomplete.state) : 0;
          const required = firstIncomplete ? Number(firstIncomplete.group.requiredQuantity) : 0;
          addButton.disabled = true;
          const remaining = Math.max(0, required - done);
          addButton.textContent = isEditMode
            ? (editReady
                ? 'Selecciona ' + remaining + (remaining === 1 ? ' tono' : ' tonos')
                : 'Cargando kit...')
            : 'Selecciona ' + required + ' tonos (' + done + '/' + required + ')';
        } else if (config.backendReady === true) {
          if (isEditMode) {
            addButton.disabled = !editReady;
            if (editReady) {
              addButton.removeAttribute('disabled');
              addButton.removeAttribute('aria-disabled');
            }
            addButton.textContent = 'Guardar cambio';
          } else {
            addButton.disabled = addButton.dataset.jd1OriginalDisabled === 'true';
            addButton.textContent = addButton.dataset.jd1OriginalText;
          }
        } else {
          addButton.disabled = true;
          addButton.textContent = 'Selección lista';
        }
      }
    };

    config.groups.forEach((group, index) => {
      const required = Number(group.requiredQuantity) || 1;
      if (required <= 1) return;

      const fieldset = [...picker.querySelectorAll('fieldset.option-selector')]
        .find((node) => node.dataset.option === group.optionName);
      if (!fieldset) return;

      hasMulti = true;
      const state = new Map();
      const lockedCounts = new Map();
      states[index] = state;

      const dual = fieldset.closest('dual-swatch-selector');
      const dropdown = dual?.querySelector('.dual-option-selector__dropdown');
      if (dropdown) dropdown.hidden = true;

      if (dropdown && !dropdown.dataset.jd1DropdownBound) {
        dropdown.dataset.jd1DropdownBound = 'true';
        dropdown.addEventListener('change', (event) => {
          event.stopPropagation();
          setTimeout(() => {
            const selectedOption = dropdown.querySelector('.custom-select__option[aria-selected="true"]');
            const value = selectedOption?.dataset.value;
            if (!value || !(value in (group.values || {}))) return;
            if (totalFor(state) >= required) return;
            state.set(value, (state.get(value) || 0) + 1);
            showMediaForValue(config, value);
            updateAll();
          }, 0);
        });
      }

      const legend = fieldset.querySelector('legend');
      if (legend) legend.classList.add('visually-hidden');

      const btns = fieldset.querySelector('.option-selector__btns');
      if (!btns) return;

      const heading = document.createElement('div');
      heading.className = 'jd1-mix-heading';
      heading.innerHTML = '<strong>Elige ' + required + ' tonos</strong>';

      const progress = document.createElement('span');
      progress.className = 'jd1-mix-progress';
      heading.appendChild(progress);

      const selected = document.createElement('div');
      selected.className = 'jd1-mix-selected';

      const counter = document.createElement('div');
      counter.className = 'jd1-mix-counter';
      counter.hidden = true;

      const counterLabel = document.createElement('div');
      counterLabel.className = 'jd1-mix-counter-label';

      const counterPrefix = document.createElement('span');
      counterPrefix.textContent = isEditMode ? 'Nuevo tono' : 'Color';

      const counterName = document.createElement('strong');
      counterName.className = 'jd1-mix-counter-name';
      counterLabel.append(counterPrefix, counterName);

      const counterControls = document.createElement('div');
      counterControls.className = 'jd1-mix-counter-controls';

      const counterMinus = document.createElement('button');
      counterMinus.type = 'button';
      counterMinus.className = 'jd1-mix-counter-btn';
      counterMinus.textContent = '−';

      const counterQty = document.createElement('span');
      counterQty.className = 'jd1-mix-counter-qty';
      counterQty.textContent = '0';

      const counterPlus = document.createElement('button');
      counterPlus.type = 'button';
      counterPlus.className = 'jd1-mix-counter-btn';
      counterPlus.textContent = '+';

      counterControls.append(counterMinus, counterQty, counterPlus);
      counter.append(counterLabel, counterControls);

      const addTone = (value) => {
        if (!value || totalFor(state) >= required) return;

        state.set(value, (state.get(value) || 0) + 1);
        showMediaForValue(config, value);
        updateAll();
      };

      const removeTone = (value) => {
        if (!value) return;

        const current = state.get(value) || 0;
        if (current < 1) return;

        if (current <= 1) state.delete(value);
        else state.set(value, current - 1);

        updateAll();
      };

      const groupWrap = document.createElement('div');
      groupWrap.className = 'jd1-mix-group';
      btns.parentNode.insertBefore(groupWrap, btns);
      groupWrap.append(heading, btns, counter, selected);

      btns.hidden = false;

      const groupState = {
        group,
        state,
        lockedCounts,
        fieldset,
        heading,
        progress,
        selected,
        counter,
        counterName,
        counterQty,
        counterMinus,
        counterPlus,
        activeValue: null,
        editMode: isEditMode,
        config,
        onChange: updateAll
      };
      groupStates.push(groupState);

      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.removeAttribute('required');
        input.tabIndex = -1;
        input.checked = false;
      });

      fieldset.addEventListener('click', (event) => {
        const label = event.target.closest('label[for]');
        if (!label || !fieldset.contains(label)) return;

        const input = document.getElementById(label.htmlFor);
        if (!input || !(input.value in (group.values || {}))) return;

        event.preventDefault();
        event.stopPropagation();

        groupState.activeValue = input.value;

        const currentQty = state.get(input.value) || 0;

        if (currentQty === 0 && totalFor(state) < required) {
          addTone(input.value);
        } else {
          showMediaForValue(config, input.value);
          updateAll();
        }
      }, true);

      counterMinus.addEventListener('click', () => {
        removeTone(groupState.activeValue);
      });

      counterPlus.addEventListener('click', () => {
        addTone(groupState.activeValue);
      });
    });

    if (!hasMulti) return;

    if (isEditMode) {
      if (!window.jd1EditLifecycleBound) {
        window.jd1EditLifecycleBound = true;

        window.addEventListener('pageshow', () => {
          ensureEditKitStillExists(editKitId);
        });

        window.addEventListener('focus', () => {
          ensureEditKitStillExists(editKitId);
        });

        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            ensureEditKitStillExists(editKitId);
          }
        });

        document.addEventListener('on:line-item:change', () => {
          ensureEditKitStillExists(editKitId);
        });
      }

      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = 'Cargando kit...';
      }

      loadEditKit(config, groupStates, editKitId, replaceVariantId)
        .then((context) => {
          editContext = context;
          editReady = true;

          groupStates.forEach((groupState) => {
            const title = groupState.heading.querySelector('strong');
            if (title) title.textContent = 'Ajusta los tonos';
          });

          updateAll();
        })
        .catch((error) => {
          console.error('[JD1 Mix Match]', error);
          const errorEl = form?.querySelector('.js-form-error');

          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = error?.message || 'No se pudo cargar el kit.';
          }

          if (addButton) {
            addButton.disabled = true;
            addButton.textContent = 'No se pudo cargar el kit';
          }
        });
    }

    if (form && config.mode === 'line_items') {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        const complete = groupStates.every(
          (groupState) => totalFor(groupState.state) === Number(groupState.group.requiredQuantity)
        );
        if (!complete) return;

        const originalText = addButton?.dataset.jd1OriginalText || addButton?.textContent || 'Agregar al carrito';

        if (addButton) {
          addButton.disabled = true;
          addButton.setAttribute('aria-disabled', 'true');
          addButton.classList.add('is-loading');
          addButton.textContent = 'Agregando...';
        }

        const errorEl = form.querySelector('.js-form-error');
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }

        try {
          if (isEditMode) {
            if (!editReady || !editContext) return;
            await replaceMixItem({ config, states, editContext, addButton });
          } else {
            await addMixItems({ config, states, addButton });
            groupStates.forEach((groupState) => groupState.state.clear());
            updateAll();
          }
        } catch (error) {
          console.error('[JD1 Mix Match]', error);
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = error?.message || 'No se pudo agregar el kit al carrito.';
          }
        } finally {
          if (addButton) {
            addButton.classList.remove('is-loading');
            addButton.removeAttribute('aria-disabled');
            addButton.textContent = originalText;
            updateAll();
          }
        }
      }, true);
    }

    initialized.add(picker);
    updateAll();
  }

  function init(root = document) {
    root.querySelectorAll?.(SELECTOR).forEach(initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  const observer = new MutationObserver(() => init());
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();